import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import jexlPackage from 'jexl'

dayjs.extend(utc)

type Scope = {
  $data: unknown
  $root: Record<string, unknown>
  $index?: number
}

type JsonObject = Record<string, unknown>
const DROP = Symbol('drop-node')
const jexl = new jexlPackage.Jexl()

// AEL-compatible helpers used by current templates/tests.
jexl.addFunction('formatNumber', (value: unknown, decimals: unknown) => {
  const num = toNumber(value)
  if (num === undefined) return undefined
  const places = Math.max(0, Math.trunc(toNumber(decimals) ?? 0))
  return num.toFixed(places)
})

jexl.addFunction('formatDateTime', (value: unknown, format: unknown) => {
  if (value === undefined || value === null) return undefined
  const parsed = dayjs(String(value))
  if (!parsed.isValid()) return String(value)
  const fmt = typeof format === 'string' ? format : 'yyyy-MM-dd'
  return parsed.format(toDayjsFormat(fmt))
})

jexl.addFunction('formatEpoch', (value: unknown, format: unknown) => {
  const epoch = toNumber(value)
  if (epoch === undefined) return undefined
  const parsed = epoch > 1_000_000_000_000 ? dayjs(epoch) : dayjs.unix(epoch)
  if (!parsed.isValid()) return String(value)
  const fmt = typeof format === 'string' ? format : 'yyyy-MM-dd'
  return parsed.format(toDayjsFormat(fmt))
})

jexl.addFunction('formatTicks', (ticks: unknown, format: unknown) => {
  const tickValue = toNumber(ticks)
  if (tickValue === undefined) return undefined
  const unixMs = (tickValue - 621355968000000000) / 10000
  const parsed = dayjs.utc(unixMs)
  if (!parsed.isValid()) return String(ticks)
  const fmt = typeof format === 'string' ? format : 'yyyy-MM-dd'
  return parsed.format(toDayjsFormat(fmt))
})

jexl.addFunctions({
  sum: (arr: unknown) => (Array.isArray(arr) ? arr.reduce((a, b) => a + (toNumber(b) ?? 0), 0) : 0),
  min: (arr: unknown) => {
    const values = numericValues(arr)
    return values.length ? Math.min(...values) : undefined
  },
  max: (arr: unknown) => {
    const values = numericValues(arr)
    return values.length ? Math.max(...values) : undefined
  },
  if: (cond: unknown, a: unknown, b: unknown) => (cond ? a : b),
  int: (value: unknown) => {
    const parsed = parseInt(String(value ?? ''), 10)
    return Number.isNaN(parsed) ? undefined : parsed
  },
  string: (value: unknown) => String(value ?? ''),
})

// Lambda shim for AEL-style select(arr, x, x.total).
jexl.addFunction('__select', (source: unknown, variableName: unknown, lambda: unknown) => {
  if (!Array.isArray(source)) return []
  if (typeof variableName !== 'string' || typeof lambda !== 'string') return []

  return source.map((item) => {
    try {
      return jexl.evalSync(lambda, {
        [variableName]: item,
      })
    } catch {
      return undefined
    }
  })
})

export function expandTemplate(template: unknown, data: Record<string, unknown>): unknown {
  return walk(template, {
    $data: data,
    $root: data,
  })
}

function walk(node: unknown, scope: Scope): unknown {
  if (typeof node === 'string') return interpolate(node, scope)

  if (Array.isArray(node)) {
    return node
      .map((item) => walk(item, scope))
      .flatMap((item) => (Array.isArray(item) ? item : [item]))
      .filter((item) => item !== DROP)
  }

  if (!isObject(node)) return node

  if ('$data' in node) {
    const bound = evaluateBinding(node.$data, scope)
    const repeatTemplate = { ...node }
    delete repeatTemplate.$data

    if (Array.isArray(bound)) {
      return bound.flatMap((item, index) => {
        const repeated = walk(repeatTemplate, { ...scope, $data: item, $index: index })
        return repeated === DROP ? [] : [repeated]
      })
    }

    return walk(repeatTemplate, { ...scope, $data: bound })
  }

  if ('$when' in node && !Boolean(evaluateBinding(node.$when, scope))) return DROP

  const result: JsonObject = {}
  for (const [key, value] of Object.entries(node)) {
    if (key === '$when') continue
    const next = walk(value, scope)
    if (next !== DROP) result[key] = next
  }
  return result
}

function evaluateBinding(value: unknown, scope: Scope): unknown {
  if (typeof value !== 'string') return walk(value, scope)

  const full = extractSingleExpression(value)
  if (full !== null) {
    const evaluated = evaluateExpression(full, scope)
    return evaluated === undefined ? value : evaluated
  }
  return interpolate(value, scope)
}

function interpolate(template: string, scope: Scope): unknown {
  const full = extractSingleExpression(template)
  if (full !== null) {
    const evaluated = evaluateExpression(full, scope)
    return evaluated === undefined ? template : evaluated
  }

  return template.replace(/\$\{([\s\S]+?)\}/g, (match, expr: string) => {
    const value = evaluateExpression(expr, scope)
    return value === undefined ? match : String(value)
  })
}

function evaluateExpression(expression: string, scope: Scope): unknown {
  const rewritten = rewriteExpression(expression).trim()
  if (!rewritten) return undefined
  try {
    return jexl.evalSync(rewritten, createEvalContext(scope))
  } catch {
    return undefined
  }
}

function createEvalContext(scope: Scope): Record<string, unknown> {
  const current = isObject(scope.$data) ? scope.$data : {}
  return { ...scope.$root, ...current, ...scope }
}

function rewriteExpression(expression: string): string {
  return rewriteSelectCalls(expression)
}

function rewriteSelectCalls(expression: string): string {
  let out = ''
  let cursor = 0

  while (cursor < expression.length) {
    const start = expression.indexOf('select(', cursor)
    if (start < 0) {
      out += expression.slice(cursor)
      break
    }
    if (!isSelectCallBoundary(expression, start)) {
      out += expression.slice(cursor, start + 'select('.length)
      cursor = start + 'select('.length
      continue
    }

    out += expression.slice(cursor, start)
    const open = start + 'select('.length - 1
    const close = findClosingParen(expression, open)
    if (close < 0) {
      out += expression.slice(start)
      break
    }

    const args = splitTopLevel(expression.slice(open + 1, close))
    if (args.length !== 3) {
      out += expression.slice(start, close + 1)
    } else {
      const source = rewriteSelectCalls(args[0] ?? '')
      const variable = (args[1] ?? '').trim()
      if (!isIdentifier(variable)) {
        out += expression.slice(start, close + 1)
      } else {
        const body = rewriteSelectCalls(args[2] ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"')
        out += `__select(${source}, "${variable}", "${body}")`
      }
    }
    cursor = close + 1
  }

  return out
}

function splitTopLevel(input: string): string[] {
  const parts: string[] = []
  let quote: "'" | '"' | null = null
  let depth = 0
  let start = 0

  for (let i = 0; i < input.length; i++) {
    const ch = input.charAt(i)

    if (quote) {
      if (ch === '\\') {
        i++
        continue
      }
      if (ch === quote) quote = null
      continue
    }

    if (ch === '"' || ch === "'") {
      quote = ch
      continue
    }
    if (ch === '(') depth++
    if (ch === ')') depth--

    if (ch === ',' && depth === 0) {
      parts.push(input.slice(start, i).trim())
      start = i + 1
    }
  }

  parts.push(input.slice(start).trim())
  return parts
}

function findClosingParen(input: string, openParenIndex: number): number {
  let quote: "'" | '"' | null = null
  let depth = 0

  for (let i = openParenIndex; i < input.length; i++) {
    const ch = input.charAt(i)

    if (quote) {
      if (ch === '\\') {
        i++
        continue
      }
      if (ch === quote) quote = null
      continue
    }

    if (ch === '"' || ch === "'") {
      quote = ch
      continue
    }
    if (ch === '(') depth++
    if (ch === ')') {
      depth--
      if (depth === 0) return i
    }
  }
  return -1
}

function extractSingleExpression(value: string): string | null {
  const matches = value.match(/\$\{[\s\S]+?\}/g)
  if (!matches || matches.length !== 1 || matches[0] !== value) return null
  return value.slice(2, -1)
}

function toDayjsFormat(format: string): string {
  return format
    .replace(/yyyy/g, '__YEAR4__')
    .replace(/yy/g, '__YEAR2__')
    .replace(/dddd/g, '__WEEKDAY4__')
    .replace(/ddd/g, '__WEEKDAY3__')
    .replace(/dd/g, '__DAY2__')
    .replace(/__YEAR4__/g, 'YYYY')
    .replace(/__YEAR2__/g, 'YY')
    .replace(/__WEEKDAY4__/g, 'dddd')
    .replace(/__WEEKDAY3__/g, 'ddd')
    .replace(/__DAY2__/g, 'DD')
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

function numericValues(value: unknown): number[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => toNumber(item))
    .filter((item): item is number => item !== undefined)
}

function isIdentifier(value: string): boolean {
  return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(value)
}

function isSelectCallBoundary(value: string, index: number): boolean {
  if (index === 0) return true
  const previous = value.charAt(index - 1)
  return !/[A-Za-z0-9_$]/.test(previous)
}

function isObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
